import { motion } from "framer-motion";
import { ChevronRight, Users, Building } from "lucide-react";
import { Link } from "react-router-dom";

export default function HomePage() {
  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20 min-h-screen flex items-center">
      {/* Two Paths Section - Main Content */}
      <section className="py-20 md:py-32 w-full relative overflow-hidden" data-testid="paths-section">
        {/* Background glows for glass effect */}
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-[5%] left-[0%] w-[700px] h-[700px] bg-[#38b6ff]/20 rounded-full blur-[200px]"></div>
        <div className="absolute bottom-[5%] right-[5%] w-[600px] h-[600px] bg-[#1a5aaa]/18 rounded-full blur-[180px]"></div>
        <div className="absolute top-[40%] left-[40%] w-[400px] h-[400px] bg-[#38b6ff]/10 rounded-full blur-[150px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-[#38b6ff] rounded-full animate-pulse-glow"></span>
                Aym High Steno
              </span>
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F5F5F7]"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="hero-heading"
            >
              How Can We <span className="gradient-text">Help You?</span>
            </motion.h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Hire Path */}
            <Link to="/hire" className="block">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8 lg:p-12 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#E8E8E8]/10 border border-[#E8E8E8]/30 rounded-lg flex items-center justify-center mb-6">
                <Building className="w-8 h-8 text-[#E8E8E8]" />
              </div>
              <h2 
                className="text-2xl font-bold text-[#F5F5F7] mb-4"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Hire a Court Reporter
              </h2>
              <p className="text-[#B0B0B0] leading-relaxed mb-6">
                Need professional court reporting services? Our certified court reporters 
                handle depositions, trials, hearings, and more.
              </p>
              <ul className="space-y-3 mb-8">
                {["Certified Professionals", "Realtime Transcription", "Fast Turnaround"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#E0E0E0]">
                    <span className="w-1.5 h-1.5 bg-[#38b6ff] rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <span
                className="inline-flex items-center rounded-none border border-[#E8E8E8] text-[#E8E8E8] bg-transparent px-4 py-2 text-sm group-hover:bg-[#E8E8E8] group-hover:text-[#0D0D0D] transition-all duration-300"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid="hire-book-now-button"
              >
                Book Now
                <ChevronRight className="w-4 h-4 ml-2" />
              </span>
            </motion.div>
            </Link>

            {/* Become Path */}
            <Link to="/stenou" className="block">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-8 lg:p-12 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#38b6ff]/10 border border-[#38b6ff]/30 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[#38b6ff]" />
              </div>
              <h2 
                className="text-2xl font-bold text-[#F5F5F7] mb-4"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Become a Court Reporter
              </h2>
              <p className="text-[#B0B0B0] leading-relaxed mb-6">
                Launch your career in the legal field. Choose from three specialized 
                programs designed to get you certified and working.
              </p>
              <ul className="space-y-3 mb-8">
                {["StenoU - Court Reporting", "EditU - Scopist Training", "ProofU - Proofreading"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#E0E0E0]">
                    <span className="w-1.5 h-1.5 bg-[#38b6ff] rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <span
                className="inline-flex items-center rounded-none bg-[#38b6ff] text-white px-4 py-2 text-sm font-semibold btn-neon"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid="programs-explore-button"
              >
                Explore Programs
                <ChevronRight className="w-4 h-4 ml-2" />
              </span>
            </motion.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
